import { useRef, useEffect } from 'react';

interface CategoryFiltersProps {
  selectedCategory: string | null;
  onCategorySelect: (category: string | null) => void;
}

const categories = [
  { id: 'sports', label: 'Sports', icon: '🏀', color: 'bg-orange-50 text-orange-600 border-orange-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'stem', label: 'STEM / Tech', icon: '🔬', color: 'bg-teal-50 text-teal-600 border-teal-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'arts', label: 'Arts / Creative', icon: '🎨', color: 'bg-pink-50 text-pink-600 border-pink-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'outdoor', label: 'Outdoor / Nature', icon: '🌿', color: 'bg-green-50 text-green-600 border-green-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'faith', label: 'Faith-Based', icon: '✝️', color: 'bg-rose-50 text-rose-600 border-rose-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'special-needs', label: 'Special Needs', icon: '❤️', color: 'bg-amber-50 text-amber-600 border-amber-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'full-day', label: 'Full-Day', icon: '☀️', color: 'bg-yellow-50 text-yellow-600 border-yellow-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'half-day', label: 'Half-Day', icon: '🕐', color: 'bg-orange-50 text-orange-600 border-orange-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
  { id: 'one-week', label: 'One-Week Camps', icon: '📅', color: 'bg-pink-50 text-pink-600 border-pink-200', activeColor: 'bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white border-transparent' },
];

export default function CategoryFilters({ selectedCategory, onCategorySelect }: CategoryFiltersProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedCategory && scrollRef.current) {
      const activeBtn = scrollRef.current.querySelector('[data-active="true"]') as HTMLElement;
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selectedCategory]);

  return (
    <div className="w-full py-5 px-1">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-filter-3-line text-[#E91E8C] text-lg"></i>
        </div>
        <h3 className="text-base font-bold text-[#3D1A6E]">Filter by Category</h3>
        {selectedCategory && (
          <button
            onClick={() => onCategorySelect(null)}
            className="ml-auto text-xs text-gray-500 hover:text-[#E91E8C] transition-colors font-semibold flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-circle-line"></i>
            Clear
          </button>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex flex-wrap gap-2.5"
      >
        {categories.map((cat) => {
          const isActive = selectedCategory === cat.id;
          return (
            <button
              key={cat.id}
              data-active={isActive ? 'true' : 'false'}
              onClick={() => onCategorySelect(isActive ? null : cat.id)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-full border font-bold text-sm
                transition-all duration-200 cursor-pointer whitespace-nowrap
                ${isActive ? cat.activeColor + ' shadow-lg scale-105' : cat.color + ' hover:shadow-sm hover:scale-102'}
              `}
            >
              <span className="text-base">{cat.icon}</span>
              <span>{cat.label}</span>
              {isActive && (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-sm"></i>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}